import React from 'react';
import { Card, CardContent } from "@/ui";
import { Shield, FileText, Lock, Eye, AlertTriangle, Users, CheckCircle, Mail } from 'lucide-react';

const PRIMARY_BLUE = "#1356a6";

export default function HIPAACompliance() {
  return (
    <div
      className="min-h-screen font-sans"
      style={{
        backgroundImage: "url('/assets/shared/simple-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        fontFamily: "Montserrat, system-ui, sans-serif",
      }}
    >
      <main className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[84rem] mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-full bg-blue-50 shadow-sm">
                <Shield className="h-8 w-8" style={{ color: PRIMARY_BLUE }} />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-normal mb-4" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
              HIPAA Compliance Statement
            </h1>
            <p className="text-lg text-gray-600" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
              <strong>Effective Date:</strong> January 8, 2026
            </p>
          </div>

          {/* Introduction */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus Inc ("AIscendus") is committed to safeguarding the privacy and security of health information and maintaining compliance with the Health Insurance Portability and Accountability Act of 1996 ("HIPAA") and applicable regulations.
              </p>
            </CardContent>
          </Card>

          {/* Section 1: Role Under HIPAA */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Shield className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  1. Role Under HIPAA
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus acts as a <strong>Business Associate</strong> to healthcare providers, health plans, and other entities subject to HIPAA ("Covered Entities").
              </p>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                As a Business Associate:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>AIscendus provides technology services, including EMR software and related infrastructure</li>
                <li>Covered Entities retain ownership and control of all Protected Health Information ("PHI")</li>
                <li>AIscendus processes PHI solely on behalf of and under the instructions of Covered Entities</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 2: Business Associate Agreements (BAAs) */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <FileText className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  2. Business Associate Agreements (BAAs)
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus enters into <strong>Business Associate Agreements (BAAs)</strong> with Covered Entities as required by HIPAA.
              </p>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Each BAA governs:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Permitted uses and disclosures of PHI</li>
                <li>Safeguards for protecting PHI</li>
                <li>Breach notification responsibilities</li>
                <li>Data return or destruction upon termination</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                In the event of a conflict between a BAA and other agreements or website terms, the <strong>BAA shall control</strong> with respect to PHI.
              </p>
            </CardContent>
          </Card>

          {/* Section 3: HIPAA Security Rule Safeguards */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Lock className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  3. HIPAA Security Rule Safeguards
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-6" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus maintains administrative, physical, and technical safeguards designed to protect the confidentiality, integrity, and availability of electronic Protected Health Information ("ePHI"), including:
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    Administrative Safeguards
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    <li>Written information security policies and procedures</li>
                    <li>Workforce training and access controls</li>
                    <li>Risk analysis and risk management processes</li>
                    <li>Incident response and breach management procedures</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    Physical Safeguards
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    <li>Secure facilities and restricted access controls</li>
                    <li>Data center and hosting environment protections</li>
                    <li>Device and media management controls</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    Technical Safeguards
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    <li>Encryption of ePHI at rest and in transit</li>
                    <li>Unique user authentication and role-based access</li>
                    <li>Audit logging and monitoring</li>
                    <li>Automatic session timeouts</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 4: Use and Disclosure of PHI */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Eye className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  4. Use and Disclosure of PHI
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus uses and discloses PHI only:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>As permitted by HIPAA</li>
                <li>As required to provide contracted services</li>
                <li>As expressly authorized by the Covered Entity</li>
                <li>As required by law</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus does <strong>not</strong> use PHI for advertising, marketing, or data monetization.
              </p>
            </CardContent>
          </Card>

          {/* Section 5: Breach Notification */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <AlertTriangle className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  5. Breach Notification
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                In accordance with HIPAA:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>AIscendus maintains procedures to identify, investigate, and respond to security incidents</li>
                <li>Covered Entities are notified <strong>without unreasonable delay</strong> following confirmation of a reportable breach of unsecured PHI</li>
                <li>Notifications include required information to support Covered Entity compliance obligations</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 6: Subcontractors */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Users className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  6. Subcontractors
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus may engage subcontractors to support service delivery. All subcontractors:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Are subject to written agreements</li>
                <li>Are required to implement safeguards consistent with HIPAA</li>
                <li>Are bound by confidentiality and security obligations</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 7: Patient Rights */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Users className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  7. Patient Rights
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus does not maintain a direct relationship with patients and does not make decisions regarding patient rights requests.
              </p>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Patients seeking to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Access medical records</li>
                <li>Request amendments</li>
                <li>Restrict disclosures</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                should contact their healthcare provider directly.
              </p>
            </CardContent>
          </Card>

          {/* Section 8: Ongoing Compliance */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <CheckCircle className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  8. Ongoing Compliance
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus regularly reviews and updates its security and privacy practices to maintain compliance with HIPAA and evolving industry standards.
              </p>
            </CardContent>
          </Card>

          {/* Section 9: Contact Information */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Mail className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  9. Contact Information
                </h2>
              </div>
              <div className="space-y-2" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <p className="text-base font-semibold text-gray-900">
                  <strong>AIscendus Inc</strong>
                </p>
                <p className="text-base text-gray-700">
                  Email: <a href="mailto:info@aiscendus.ai" className="text-blue-600 hover:underline">info@aiscendus.ai</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

